import { useAuthState } from '@/app/providers/auth'
import type { BasketFragment } from '@/generated/graphql'
import { getDeliveryDetailsForMixpanel, useMixPanel } from '@/helpers/mixpanelConfig'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import useTenant from '@/hooks/useTenant'

import { useCallback } from 'react'

import type { UserBasketSettings } from '../../common/domain/user-basket-settings'
import type { PastOrderForReorder } from '../components/reorder-drawer/use-past-orders'

// PascalCase values to match the `supplierType` / `source` props on the sibling events.
export type ReorderBasketState = 'NewBasket' | 'EmptyBasket' | 'NonEmptyBasket'

export const getReorderBasketState = (
  activeBasket: Pick<BasketFragment, 'items'> | null | undefined,
): ReorderBasketState => {
  if (!activeBasket) return 'NewBasket'
  if (activeBasket.items.length === 0) return 'EmptyBasket'
  return 'NonEmptyBasket'
}

interface LoadPastOrderContext {
  vendor: {
    id: string
    companyName: string
    isPantry: boolean | null
  }
  orderSettings: UserBasketSettings
}

interface LoadPastOrderEventParams {
  order: PastOrderForReorder
  basketState: ReorderBasketState
}

export const useMixpanelLoadPastOrder = ({ vendor, orderSettings }: LoadPastOrderContext) => {
  const mixPanel = useMixPanel()
  const tenant = useTenant()
  const authState = useAuthState()
  const loadPastOrderEnabled = useFeatureFlag(SPLITS.MIXPANEL_LOAD_PAST_ORDER)

  const trackLoadPastOrder = useCallback(
    ({ order, basketState }: LoadPastOrderEventParams) => {
      if (!loadPastOrderEnabled || !vendor) return

      try {
        const deliveryDetails = getDeliveryDetailsForMixpanel(orderSettings)
        const items = order.orderItems.items
        mixPanel?.track('Load Past Order', {
          name: 'Load Past Order',
          ...deliveryDetails,
          pastOrderId: order.id,
          orderId: order.orderId,
          itemCount: items.length,
          totalQuantity: items.reduce((sum, item) => sum + (item.qty ?? 0), 0),
          basketState,
          supplierType: vendor.isPantry ? 'Pantry' : 'Catering',
          address: orderSettings.location,
          customerAccount:
            authState.type === 'authenticated'
              ? `${authState.user?.id}-${authState.user.account?.name}`
              : 'guest',
          date: orderSettings.date,
          headCount: orderSettings.headCount,
          tenant: tenant?.name,
          vendor: vendor.companyName,
          vendorId: vendor.id,
        })
      } catch (e) {
        console.error('Mixpanel tracking failed', e)
      }
    },
    [loadPastOrderEnabled, mixPanel, authState, tenant, vendor, orderSettings],
  )

  return { trackLoadPastOrder }
}
